import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const BulkAssignDialog = ({ open, onClose, onSuccess, selectedLeads, executives }) => {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState('');

  const handleAssign = async () => {
    if (!selectedExecutive) {
      toast.error('Please select an executive');
      return;
    }

    const exec = executives.find((e) => e.id === selectedExecutive);
    if (!exec) return;

    setSaving(true);
    try {
      const response = await api.post('/leads/bulk-assign', {
        lead_ids: selectedLeads,
        executiveId: selectedExecutive,
        executiveName: exec.name,
      });
      toast.success(response.data.message || `${selectedLeads.length} leads assigned to ${exec.name}`);
      onSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to assign leads';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      console.error('Bulk assign error:', err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Assign Leads</DialogTitle>
          <DialogDescription>
            Assign {selectedLeads.length} selected lead(s) to an executive
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label>Assign To</Label>
          <Select value={selectedExecutive} onValueChange={setSelectedExecutive}>
            <SelectTrigger data-testid="bulk-assign-executive">
              <SelectValue placeholder="Select Executive" />
            </SelectTrigger>
            <SelectContent>
              {executives.map((exec) => (
                <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={saving || !selectedExecutive} data-testid="confirm-bulk-assign">
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )}
            Assign Leads
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAssignDialog;
